import React, {useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table} from "react-bootstrap";
import Pagination from "react-bootstrap/Pagination";
import {fetchReturnedOrders} from "../api/api";
import {useNavigate} from "react-router-dom";
import {AccountingScopeSelect, useAccountingScope} from "./Accounting/accountingscope";

const money = (value) => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatDateTime = (value) => value
    ? new Date(value).toLocaleString("en-GB", {dateStyle: "medium", timeStyle: "short"})
    : "—";

const ReturnedOrders = () => {
    const scope = useAccountingScope();
    const navigate = useNavigate();
    const [returns, setReturns] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedReturn, setSelectedReturn] = useState(null);
    const pageSize = 20;

    useEffect(() => {
        if (scope.loadingScope || !scope.storeId) return undefined;
        const timer = setTimeout(async () => {
            setLoading(true);
            setError("");
            try {
                const response = await fetchReturnedOrders(currentPage, {
                    searchTerm,
                    start_date: startDate,
                    end_date: endDate,
                    store_id: scope.storeId,
                }, pageSize);
                setReturns(response?.results || []);
                setTotalCount(response?.count || 0);
            } catch (loadError) {
                console.error("Error fetching returned orders:", loadError);
                setReturns([]);
                setTotalCount(0);
                setError(loadError?.response?.data?.detail || "Unable to load order returns.");
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [currentPage, searchTerm, startDate, endDate, scope.loadingScope, scope.storeId]);

    useEffect(() => setCurrentPage(1), [scope.storeId]);

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const pageNumbers = [];
    const firstPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    for (let page = firstPage; page <= Math.min(totalPages, firstPage + 4); page += 1) {
        pageNumbers.push(page);
    }

    const pageSummary = useMemo(() => returns.reduce((summary, item) => ({
        amount: summary.amount + Number(item.total_return_amount || 0),
        quantity: summary.quantity + Number(item.total_return_quantity || 0),
        lines: summary.lines + (item.returned_items?.length || 0),
    }), {amount: 0, quantity: 0, lines: 0}), [returns]);

    const resetFilters = () => {
        setSearchTerm("");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
    };

    return (
        <div className="container-fluid px-3 px-lg-4 py-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div>
                    <div className="text-uppercase text-muted small fw-semibold">Sales reports</div>
                    <h1 className="mb-1">Order returns</h1>
                    <p className="text-muted mb-0">Trace refunded products, values, operators and originating payments.</p>
                </div>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>Back to reports</Button>
            </div>

            <Row className="g-3 mb-4">
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm"><Card.Body>
                        <div className="text-muted small">Matching return records</div>
                        <div className="display-6 fw-semibold">{totalCount.toLocaleString()}</div>
                        <div className="small text-muted">Across the selected filters</div>
                    </Card.Body></Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm"><Card.Body>
                        <div className="text-muted small">Refund value on this page</div>
                        <div className="h2 fw-semibold mb-1">KES {money(pageSummary.amount)}</div>
                        <div className="small text-muted">Discount-adjusted return value</div>
                    </Card.Body></Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm"><Card.Body>
                        <div className="text-muted small">Quantity on this page</div>
                        <div className="display-6 fw-semibold">{pageSummary.quantity.toLocaleString()}</div>
                        <div className="small text-muted">{pageSummary.lines} returned product lines</div>
                    </Card.Body></Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        {scope.isAdmin && <Col lg={3}><AccountingScopeSelect scope={scope} label="Return scope" /></Col>}
                        <Col lg={scope.isAdmin ? 3 : 4}>
                            <Form.Label>Search</Form.Label>
                            <Form.Control
                                value={searchTerm}
                                placeholder="Order, customer, product or operator"
                                onChange={(event) => { setSearchTerm(event.target.value); setCurrentPage(1); }}
                            />
                        </Col>
                        <Col lg={2}>
                            <Form.Label>From</Form.Label>
                            <Form.Control type="date" value={startDate} onChange={(event) => { setStartDate(event.target.value); setCurrentPage(1); }} />
                        </Col>
                        <Col lg={2}>
                            <Form.Label>To</Form.Label>
                            <Form.Control type="date" value={endDate} onChange={(event) => { setEndDate(event.target.value); setCurrentPage(1); }} />
                        </Col>
                        <Col lg={2}><Button variant="outline-secondary" className="w-100" onClick={resetFilters}>Clear filters</Button></Col>
                    </Row>
                </Card.Body>
            </Card>

            {scope.scopeError && <Alert variant="danger">{scope.scopeError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /><div className="mt-2 text-muted">Loading returns…</div></div>
                    ) : returns.length ? (
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="table-light"><tr>
                                <th>Order</th><th>Customer / Store</th><th>Returned products</th><th>Return date</th>
                                <th>Payment</th><th className="text-end">Refund value</th><th>Processed by</th><th></th>
                            </tr></thead>
                            <tbody>{returns.map((item) => (
                                <tr key={item.id}>
                                    <td><strong>{item.order_number || item.order}</strong><div className="small text-muted">Sold {formatDateTime(item.original_order_date)}</div></td>
                                    <td>{item.customer_name || "Walk-in"}<div className="small text-muted">{item.store_name || "—"}</div></td>
                                    <td>
                                        {(item.returned_items || []).slice(0, 2).map((line, index) => (
                                            <div key={`${item.id}-${index}`} className="small">
                                                {line.product_details?.product_name || "Product"} × {Number(line.quantity).toLocaleString()}
                                            </div>
                                        ))}
                                        {(item.returned_items?.length || 0) > 2 && <Badge bg="light" text="dark">+{item.returned_items.length - 2} more</Badge>}
                                    </td>
                                    <td>{formatDateTime(item.return_date)}</td>
                                    <td>{item.payment_method || "—"}</td>
                                    <td className="text-end fw-semibold">KES {money(item.total_return_amount)}</td>
                                    <td>{item.processed_by || "—"}</td>
                                    <td><Button size="sm" variant="outline-primary" onClick={() => setSelectedReturn(item)}>Details</Button></td>
                                </tr>
                            ))}</tbody>
                        </Table>
                    ) : <div className="text-center py-5 text-muted">No order returns match the selected scope and filters.</div>}
                </Card.Body>
            </Card>

            {totalCount > pageSize && <Pagination className="justify-content-center mt-4">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))} disabled={currentPage === 1} />
                {pageNumbers.map((page) => <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>{page}</Pagination.Item>)}
                <Pagination.Next onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>}

            <Modal show={Boolean(selectedReturn)} onHide={() => setSelectedReturn(null)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>Return {selectedReturn?.id}</Modal.Title></Modal.Header>
                <Modal.Body>{selectedReturn && <>
                    <Row className="g-3 mb-3">
                        <Col sm={6}><div className="text-muted small">Original order</div><strong>{selectedReturn.order_number}</strong></Col>
                        <Col sm={6}><div className="text-muted small">Refund value</div><strong>KES {money(selectedReturn.total_return_amount)}</strong></Col>
                        <Col sm={6}><div className="text-muted small">Customer</div>{selectedReturn.customer_name || "Walk-in"}</Col>
                        <Col sm={6}><div className="text-muted small">Store</div>{selectedReturn.store_name}</Col>
                        <Col sm={6}><div className="text-muted small">Returned</div>{formatDateTime(selectedReturn.return_date)}</Col>
                        <Col sm={6}><div className="text-muted small">Processed by</div>{selectedReturn.processed_by || "—"}</Col>
                    </Row>
                    <Table responsive bordered size="sm"><thead><tr><th>Product</th><th>Unit</th><th className="text-end">Quantity</th><th className="text-end">Unit price</th></tr></thead>
                        <tbody>{(selectedReturn.returned_items || []).map((line, index) => <tr key={index}>
                            <td>{line.product_details?.product_name}</td><td>{line.unit_name || line.unit_abbreviation || "Base unit"}</td>
                            <td className="text-end">{Number(line.quantity).toLocaleString()}</td><td className="text-end">KES {money(line.unit_price)}</td>
                        </tr>)}</tbody>
                    </Table>
                </>}</Modal.Body>
            </Modal>
        </div>
    );
};

export default ReturnedOrders;
